import RenderEntryValueItem from './RenderEntryValueItem';
import { ConfigEntryType, ConfigType } from 'config/config';
import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { ConfigAPI, CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { KeyboardEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';

type RenderEntryValuesProps = {
  entry: ConfigEntryType;
  isCompanyConfig: boolean;
};

function RenderEntryValues({ entry, isCompanyConfig }: RenderEntryValuesProps) {
  const { companyId } = useUserData();
  const { updateConfigCache, config } = useConfigQuery({
    ...(isCompanyConfig && { companyId }),
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const [openAddMilestone, setOpenAddMilestone] = useState(false);
  const [value, setValue] = useState('');
  const [isAdding, setAdding] = useState(false);

  const updateConfigEntriesOrder = async (entryValues: string[]) => {
    try {
      const updatedEntries = (config?.entries || []).map((_entry) =>
        _entry.id === entry.id ? { ..._entry, entryValues } : _entry,
      );
      updateConfigCache({
        updatedConfig: {
          config: { ...config, entries: updatedEntries } as ConfigType,
        },
      });
      if (config) {
        const { data } = await ConfigAPI.updateConfig({
          ...config,
          ...(isCompanyConfig && {
            companyId,
          }),
          entries: updatedEntries,
        });
        updateConfigCache({
          updatedConfig: {
            config: data.config,
          },
        });
      }
    } catch (e) {
      updateConfigCache({
        updatedConfig: {
          config,
        },
      });
      toast.error(catchErrorMessage(e));
    }
  };

  const onAddConfigEntryValue = async () => {
    try {
      if (config) {
        setAdding(true);
        const { data } = await ConfigAPI.updateConfig({
          ...config,
          entries: config.entries.map((_entry) =>
            _entry.id === entry.id
              ? { ..._entry, entryValues: [..._entry.entryValues, value.trim()] }
              : _entry,
          ),
        });
        updateConfigCache({
          updatedConfig: {
            config: data.config,
          },
        });
        setValue('');
        setOpenAddMilestone(false);
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  const handleEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onAddConfigEntryValue();
    }
  };

  return (
    <Box mt={0.5}>
      <DndContext
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over?.id) {
            const activeIndex = (entry.entryValues || []).findIndex((value) => value === active.id);
            const overIndex = (entry.entryValues || []).findIndex((value) => value === over.id);

            updateConfigEntriesOrder(arrayMove(entry.entryValues || [], activeIndex, overIndex));
          }
        }}
      >
        <SortableContext items={(entry.entryValues || []).map((value) => ({ id: value || '' }))}>
          {(entry.entryValues || []).map((value) => (
            <RenderEntryValueItem
              key={value}
              entry={entry}
              value={value}
              isCompanyConfig={isCompanyConfig}
            />
          ))}
        </SortableContext>
      </DndContext>

      {openAddMilestone ? (
        <Box display="flex" alignItems="center" mt={1} ml={4.5} mr={1}>
          <TextField
            autoFocus
            size="small"
            placeholder="Enter milestone..."
            value={value}
            onKeyUp={handleEnter}
            onChange={(event) => setValue(event?.target.value)}
            type="text"
            variant="outlined"
            fullWidth
          />

          <Box display="flex" alignItems="center">
            <Button
              sx={{ ml: 2 }}
              onClick={() => {
                setValue('');
                setOpenAddMilestone(false);
              }}
            >
              Cancel
            </Button>
            <Button
              sx={{ ml: 2 }}
              variant="outlined"
              disabled={!value || isAdding}
              onClick={onAddConfigEntryValue}
            >
              {isAdding ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : (
                'Add'
              )}
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          size="small"
          startIcon={<AddLineIcon size={18} />}
          sx={(theme) => ({ color: theme.palette.custom.link, ml: 3, mt: 0.5 })}
          onClick={() => setOpenAddMilestone(true)}
        >
          Add Milestone
        </Button>
      )}
    </Box>
  );
}

export default RenderEntryValues;
